import { useState } from "react";

/**
 * UserMark — the person mark.
 *
 * One circle, three tiers: the photo when `profilePicture` resolves, a
 * two-letter monogram when it does not, one glyph when the name cannot give
 * two. All three share the box and the hairline, because a mixed wall of faces
 * and monograms is the NORMAL case — password sign-ups store an empty string —
 * and a monogram row must never look unfinished beside a photo row.
 *
 * A person is a CIRCLE and a brand is a SQUARE. That is the only shape
 * exception in the system, and it earns itself: it tells you whether a row is a
 * who or a what before you read either.
 *
 * The photo is a Google URL written on OAuth login, not something we host, so
 * onError falling back to the monogram is the EXPECTED path for some viewers
 * (rate limits, referrer policy), not an edge case.
 */

/** Initials of the first two words, or the first two characters of one word. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  const letters = words.length > 1 ? words[0][0] + words[1][0] : words[0].slice(0, 2);
  return letters.toUpperCase();
}

export function UserMark({
  name,
  photoUrl,
  size = 26,
  /** Renders the attention dot over the mark's corner. */
  flagged = false,
  /** Ring colour for the flag punch-out — match whatever surface it sits on. */
  ringColor = "var(--co-rail)",
  /** The nav rail's Avatar (library 01, rail footer): co-surface fill, initials
   *  in Manrope 700 10 co-text. Everywhere else keeps the 12px floor. */
  rail = false,
  /** The profile menu's Avatar (library 01, Nav / Profile menu): a radius-12
   *  tile on co-tonal with no hairline, initials in Manrope 700 12 co-text2. */
  menu = false,
}: {
  name: string;
  photoUrl?: string | null;
  size?: number;
  flagged?: boolean;
  ringColor?: string;
  rail?: boolean;
  menu?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const showPhoto = !!photoUrl && !failed;

  return (
    <span className="relative inline-flex shrink-0" aria-hidden="true">
      <span
        style={{ width: size, height: size }}
        className={`grid shrink-0 place-items-center overflow-hidden ${menu ? "rounded-[12px] bg-co-tonal" : `rounded-full border border-co-line2 ${rail ? "bg-co-surface" : "bg-co-surface-solid"}`}`}
      >
        {showPhoto ? (
          <img
            src={photoUrl as string}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setFailed(true)}
            className="size-full object-cover"
          />
        ) : (
          // Avatar (library, style B): initials in Manrope 700, never under the
          // 12px floor.
          <span
            className={`font-body font-bold leading-none ${rail ? "text-co-text" : "text-co-text2"}`}
            style={{ fontSize: rail ? 10 : menu ? 12 : Math.max(12, Math.round(size * 0.46)) }}
          >
            {initials(name)}
          </span>
        )}
      </span>

      {/* Overlaid, never a sibling. As a sibling it takes width in the rail's
          flex row and knocks the collapsed mark off the icon column's centre. */}
      {flagged ? (
        <span
          className="absolute rounded-full bg-co-alert-ink"
          style={{ top: -1, right: -1, width: 8, height: 8, border: `2px solid ${ringColor}` }}
        />
      ) : null}
    </span>
  );
}

export default UserMark;
